import React, { type ReactNode } from 'react';
import { ff } from '@atlassian/jira-feature-flagging';
import { ITEM_TYPE, CREATE_ITEM_TYPE } from '../../constants';
import { useVirtualisedItems, useIsScrollingY } from '../../context';
import type { EnrichedItem, FlatItem, FlattenedEnrichedItem } from '../../types';
import {
	getChildCreateMetaIfContainerVisible,
	getChildItem,
	isParentCreateItem,
} from './common/get-child-item';
import { getPlaceholderCreateItem } from './common/get-placeholder-create-item';
import type { RenderCreateItem, RenderItem, RenderItemContainer } from './common/types';

type Props = {
	renderItem: RenderItem;
	renderItemContainer: RenderItemContainer;
	renderCreateItem: RenderCreateItem;
};

type RenderProps = {
	items: FlattenedEnrichedItem[];
	getIsVirtual: (flatIndex: number) => boolean;
	parentItem: EnrichedItem<FlatItem> | undefined;
};

const ItemsRenderer = ({ renderItem, renderItemContainer, renderCreateItem }: Props) =>
	ff('jwm.visual-hierarchy-milestone-1')
		? ItemsRendererRecursive({ renderItem, renderItemContainer, renderCreateItem })
		: ItemsRendererOld({ renderItem, renderItemContainer, renderCreateItem });

const ItemsRendererRecursive = ({ renderItem, renderItemContainer, renderCreateItem }: Props) => {
	const [virtualisedData] = useVirtualisedItems();

	const { requiredItems = [], overscanRange } = virtualisedData;

	// if the item is beyond the visible and overscan range, then it's not visible
	// remove overscanRange undefined checking when cleaning up jwm.visual-hierarchy-milestone-1
	const getIsVirtual = (flatIndex: number) =>
		overscanRange ? flatIndex < overscanRange.start || flatIndex > overscanRange.end : false;

	const renderedItems = renderList({
		items: requiredItems,
		getIsVirtual,
		parentItem: undefined,
		renderItem,
		renderItemContainer,
		renderCreateItem,
	});
	return <>{renderedItems}</>;
};

const renderList = ({
	items,
	getIsVirtual,
	parentItem,
	renderItem,
	renderItemContainer,
	renderCreateItem,
}: Props & RenderProps) => {
	// Start iterate flat items
	const flatItemElements: ReactNode[] = [];

	const getItemsToRender = (
		parentFlatItem: EnrichedItem<FlatItem> | undefined,
		flatItems: FlattenedEnrichedItem[],
	) => {
		if (parentFlatItem === undefined) {
			return flatItems.filter(({ meta }) => !meta.parentId);
		}
		return flatItems.filter(({ meta }) => meta.parentId === parentFlatItem.item.id);
	};

	const itemsToRender = getItemsToRender(parentItem, items);

	// Generate items
	itemsToRender?.forEach((enrichedItem) => {
		if (enrichedItem.type === ITEM_TYPE) {
			const itemContainerChildren: ReactNode[] = [];

			// remove flatIndex initial value when cleaning up jwm.visual-hierarchy-milestone-1
			const { item, meta, flatIndex = 0 } = enrichedItem;

			// If it is an invisible sibling or ancestor we render it as virtual,
			itemContainerChildren.push(renderItem(item, meta, flatIndex, getIsVirtual(flatIndex)));

			const {
				isExpanded,
				isParent,
				numChildren = 0,
				ariaRowIndex,
				startOffset,
				childItemsHeight,
			} = meta;

			if (isExpanded && numChildren > 0) {
				const renderedChildItems = renderList({
					items,
					getIsVirtual,
					parentItem: enrichedItem,
					renderItem,
					renderItemContainer,
					renderCreateItem,
				});
				itemContainerChildren.push(renderedChildItems);
			}

			const parentItemContainerMeta = {
				parentId: parentItem?.item.id,
				isExpanded,
				isParent,
				id: item.id,
				level: item.level,
				depth: item.depth,
				ariaRowIndex,
				startOffset,
				childItemsHeight,
				isInTransition: item.isInTransition,
			};

			flatItemElements.push(renderItemContainer(parentItemContainerMeta, 0, itemContainerChildren));
		} else if (enrichedItem.type === CREATE_ITEM_TYPE) {
			// Render visible create item
			flatItemElements.push(renderCreateItem(enrichedItem.anchorItem, enrichedItem.meta));
		}
	});
	return flatItemElements;
};

const ItemsRendererOld = ({ renderItem, renderItemContainer, renderCreateItem }: Props) => {
	const [virtualisedData] = useVirtualisedItems();
	const [isScrollingY] = useIsScrollingY();

	const { createItem, visibleItems, overscan, orphanIndexOffset, orphanParent, createPosition } =
		virtualisedData;

	const getIsVirtual = (index: number) =>
		isScrollingY && index < overscan && index >= visibleItems.length - overscan - 1;

	const items = orphanParent !== undefined ? [orphanParent, ...visibleItems] : visibleItems;

	const totalVisibleItems = items.length;

	const childCreatePosition = getChildCreateMetaIfContainerVisible(
		createPosition,
		createItem,
		items,
	);

	const placeholderCreateItem = getPlaceholderCreateItem(
		createPosition !== undefined && childCreatePosition === undefined,
		createItem,
		renderItemContainer,
		renderCreateItem,
	);

	// Start iterate flat items
	const flatItemElements = [];

	if (createPosition !== undefined && childCreatePosition === undefined) {
		flatItemElements.push(placeholderCreateItem);
	}

	// Generate items
	items.forEach((enrichedItem, index) => {
		const isVirtual = getIsVirtual(index);
		if (enrichedItem.type === ITEM_TYPE) {
			const isItemOrphanChild = orphanParent === enrichedItem;
			const parentItem =
				isItemOrphanChild && orphanParent !== undefined ? orphanParent : enrichedItem;

			const { item, meta, flatIndex } = parentItem;

			const itemContainerChildren: ReactNode[] = isItemOrphanChild
				? [] // Do not render the actual parent item if it is an orphan item
				: [renderItem(item, meta, flatIndex, isVirtual)];

			const {
				isExpanded,
				isParent,
				numChildren = 0,
				ariaRowIndex,
				startOffset,
				childItemsHeight,
			} = meta;

			if (isExpanded) {
				const childStartIndex = index + 1;
				const childEndIndex =
					childStartIndex + numChildren - (isItemOrphanChild ? orphanIndexOffset : 0);

				// Get child items from flattened items with the flat index and number of children
				const childItems = items.slice(childStartIndex, Math.min(childEndIndex, totalVisibleItems));

				if (
					childCreatePosition !== undefined &&
					childCreatePosition?.parentId === item.id &&
					placeholderCreateItem !== undefined
				) {
					itemContainerChildren.push(placeholderCreateItem);
				}

				childItems.forEach((childItem, childIndex) => {
					const isChildVirtual = getIsVirtual(childStartIndex + childIndex);
					const childRenderItem = getChildItem(
						childItem,
						isChildVirtual,
						renderItem,
						renderItemContainer,
						renderCreateItem,
					);

					if (childRenderItem !== undefined) {
						itemContainerChildren.push(childRenderItem);
					}
				});
			}

			const parentItemContainerMeta = {
				parentId: undefined,
				isExpanded,
				isParent,
				id: item.id,
				level: item.level,
				ariaRowIndex,
				startOffset,
				childItemsHeight,
				isInTransition: item.isInTransition,
			};

			flatItemElements.push(
				renderItemContainer(parentItemContainerMeta, flatIndex, itemContainerChildren),
			);
		} else if (enrichedItem.type === CREATE_ITEM_TYPE && isParentCreateItem(enrichedItem)) {
			flatItemElements.push(renderCreateItem(enrichedItem.anchorItem, enrichedItem.meta));
		}
	});

	return <>{flatItemElements}</>;
};

export default ItemsRenderer;
